import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { debounce } from "lodash";
import { toast } from "react-toastify";
import { updateInstallation } from "../../../services/installationService";
import { captureWizardError } from "./shared/wizardObservability";

const DEBOUNCE_MS = 600;
const SAVED_VISIBLE_MS = 1500;

/**
 * Optimistic, debounced autosave for an installation object.
 *
 * Returns `{ installation, setField, saveStatus }`. `setField(key, value)`
 * updates local state immediately and schedules a PATCH after an idle window;
 * multiple rapid edits coalesce into one request. `saveStatus` is one of
 * "idle" | "pending" | "saving" | "saved" | "error". On 4xx the server's
 * detail is toasted; on any error the unsaved keys are re-queued for retry.
 */
export default function useInstallationAutosave(initialInstallation) {
  const [installation, setInstallation] = useState(initialInstallation);
  const [saveStatus, setSaveStatus] = useState("idle");

  const pendingPayloadRef = useRef({});
  const savedTimerRef = useRef(null);
  const identifierRef = useRef(initialInstallation?.uid ?? initialInstallation?.deal_id);
  const mountedRef = useRef(true);
  const chainRef = useRef(Promise.resolve());

  const flush = useCallback(() => {
    const payload = pendingPayloadRef.current;
    const identifier = identifierRef.current;
    if (!identifier || Object.keys(payload).length === 0) {
      if (mountedRef.current) setSaveStatus("idle");
      return chainRef.current;
    }
    pendingPayloadRef.current = {};
    if (mountedRef.current) setSaveStatus("saving");

    const next = chainRef.current.then(async () => {
      try {
        const { data } = await updateInstallation(identifier, payload);
        if (!mountedRef.current || identifierRef.current !== identifier) return;
        setInstallation((prev) => ({ ...prev, ...data }));
        setSaveStatus("saved");
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => {
          if (!mountedRef.current) return;
          setSaveStatus((current) => (current === "saved" ? "idle" : current));
        }, SAVED_VISIBLE_MS);
      } catch (err) {
        captureWizardError(err, "autosave", { identifier, fields: Object.keys(payload) });
        if (!mountedRef.current || identifierRef.current !== identifier) return;
        pendingPayloadRef.current = { ...payload, ...pendingPayloadRef.current };
        setSaveStatus("error");
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail ?? err?.response?.data?.error;
        if (status >= 400 && status < 500) {
          toast.error(detail ?? "Failed to save installation changes");
        }
      }
    });
    chainRef.current = next.catch(() => {});
    return next;
  }, []);

  const debouncedFlush = useMemo(() => debounce(flush, DEBOUNCE_MS), [flush]);

  // Reset state when the parent swaps to a different installation.
  useEffect(() => {
    const nextId = initialInstallation?.uid ?? initialInstallation?.deal_id;
    if (nextId !== identifierRef.current) {
      debouncedFlush.cancel();
      // Fire the outgoing PATCH inline (not via flush) so its response can't
      // write back into the incoming installation's state.
      const orphanedPayload = pendingPayloadRef.current;
      const orphanedIdentifier = identifierRef.current;
      if (orphanedIdentifier && Object.keys(orphanedPayload).length > 0) {
        updateInstallation(orphanedIdentifier, orphanedPayload).catch((err) =>
          captureWizardError(err, "autosave-orphaned", { identifier: orphanedIdentifier })
        );
      }
      pendingPayloadRef.current = {};
      identifierRef.current = nextId;
      setInstallation(initialInstallation);
      setSaveStatus("idle");
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    }
  }, [initialInstallation, debouncedFlush]);

  // Flush pending edits on unmount; re-arm mountedRef on mount for StrictMode.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      debouncedFlush.cancel();
      if (Object.keys(pendingPayloadRef.current).length > 0) flush();
    };
  }, [debouncedFlush, flush]);

  const setField = useCallback(
    (key, value) => {
      setInstallation((prev) => ({ ...prev, [key]: value }));
      pendingPayloadRef.current = { ...pendingPayloadRef.current, [key]: value };
      setSaveStatus("pending");
      debouncedFlush();
    },
    [debouncedFlush]
  );

  // Like setField but skips the debounce and returns the flush promise.
  const commitField = useCallback(
    (key, value) => {
      setInstallation((prev) => ({ ...prev, [key]: value }));
      pendingPayloadRef.current = { ...pendingPayloadRef.current, [key]: value };
      setSaveStatus("pending");
      debouncedFlush.cancel();
      return flush();
    },
    [debouncedFlush, flush]
  );

  // Merge server-authoritative fields into local state without scheduling a PATCH.
  const mergeServerFields = useCallback((patch) => {
    if (!patch || typeof patch !== "object") return;
    setInstallation((prev) => ({ ...prev, ...patch }));
  }, []);

  // Drain queued + in-flight PATCHes; await before a side-door POST to avoid step regressions.
  const flushPending = useCallback(async () => {
    debouncedFlush.cancel();
    if (Object.keys(pendingPayloadRef.current).length > 0) {
      flush();
    }
    await chainRef.current;
  }, [debouncedFlush, flush]);

  return { installation, setField, commitField, mergeServerFields, flushPending, saveStatus };
}
